import 'vite/modulepreload-polyfill';
import { createApp, onBeforeUnmount, onMounted, ref } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';
import { useMasterProductCard } from '~/composables/use-master-product-card';
import { useScrollLock } from '~/composables/use-scroll-lock';
import { useViewportSize } from '~/composables/use-viewport-size';
import { TRACK_COLLECTION_FILTER_TYPE } from '~/constants/rudderstack';

import { BaseSection } from './base-section';

const SECTION_NAME = 'collection-product-grid';

/**
 * Collection grid component for handling product sorting and filtering
 */
class CollectionProductGrid extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;

    createApp({
      name: 'CollectionProductGridSection',
      setup() {
        const { cardRefs, setActiveColorway } = useMasterProductCard(container);
        const { isMobile } = useViewportSize();
        const { lockScroll, unlockScroll } = useScrollLock();

        const showFilterDropdown = ref(false);
        const activeFilterDropdowns = ref(new Map());
        const filterDropdownRef = ref(null);
        const filterButtonsRef = ref([]);

        const url = new URL(window.location.href);
        const sortBy = ref(url.searchParams.get('sort_by') || 'manual');

        const handleSortByChange = (event) => {
          sortBy.value = event.target.value;
          url.searchParams.set('sort_by', event.target.value);

          // Hook analytics events for sorting,
          //  collection JSON should be avaliable on Collection page
          //  Url object is updated with query string api params
          AnalyticsData.hookProductListFeatureClicked({
            collection: window.ALLBIRDS?.page?.collectionPage?.collection,
            featureName: sortBy.value,
            featureType: TRACK_COLLECTION_FILTER_TYPE.SORTING.KEY,
            featureUrlObject: url,
            sectionName: SECTION_NAME,
            useDomData: false,
          });

          window.open(url.toString(), '_self');
        };

        const toggleFilterDropdown = (event, filterParamName) => {
          activeFilterDropdowns.value.set(
            filterParamName,
            !activeFilterDropdowns.value.get(filterParamName),
          );
        };

        const toggleFilterMenu = (event) => {
          if (!isMobile.value) {
            event.preventDefault();
            event.stopPropagation();
          }
        };

        const handleFilterDropdownChange = () => {
          showFilterDropdown.value = !showFilterDropdown.value;
          if (isMobile.value && showFilterDropdown.value) {
            lockScroll();
          }
        };

        const closeFilterDropdown = (event) => {
          if (event) {
            event.preventDefault();
          }
          showFilterDropdown.value = false;
          if (isMobile.value) {
            unlockScroll();
          }
        };

        const handleClickOutside = (event) => {
          if (
            showFilterDropdown.value &&
            filterDropdownRef.value &&
            !filterDropdownRef.value.contains(event.target) &&
            !filterButtonsRef.value.some((button) => button.contains(event.target))
          ) {
            closeFilterDropdown();
          }
        };

        onMounted(() => {
          filterDropdownRef.value = container.querySelector('[data-filter-dropdown]');
          filterButtonsRef.value = [
            container.querySelector('[data-filter-and-sort-desktop]'),
            container.querySelector('[data-filter-and-sort-mobile]'),
          ].filter(Boolean);
          document.addEventListener('click', handleClickOutside);

          // Hook analytics events, collection JSON should be avaliable on Collection page
          AnalyticsData.hookProductListClicked({
            collection: window.ALLBIRDS?.page?.collectionPage?.collection,
            sectionName: SECTION_NAME,
          });

          // Hook analytics events for Gender Toggle,
          //  collection JSON should be avaliable on Collection page
          //  anchor elements are updated with query string api params
          AnalyticsData.hookProductListFeatureClicked({
            collection: window.ALLBIRDS?.page?.collectionPage?.collection,
            featureType: TRACK_COLLECTION_FILTER_TYPE.PILL_TOGGLE.KEY,
            sectionName: SECTION_NAME,
            useDomData: true,
          });
        });

        onBeforeUnmount(() => {
          document.removeEventListener('click', handleClickOutside);
        });

        return {
          activeFilterDropdowns,
          cardRefs,
          closeFilterDropdown,
          handleFilterDropdownChange,
          handleSortByChange,
          setActiveColorway,
          showFilterDropdown,
          sortBy,
          toggleFilterDropdown,
          toggleFilterMenu,
        };
      },
    }).mount(container);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CollectionProductGrid();
});
